import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PasswordChip from './PasswordChip';
import { isOneTime } from '../roles';

// Small info button shown next to a user's name, to whoever supervises them,
// while that user is still on a one-time password. Clicking it reveals the
// password in a popover; it disappears once the user sets their own password.
// A one-time account never sets one, so for those it stays for the life of the
// account — which is the only way its password can be handed out again.
// The popover is portaled to <body> because the team cards use overflow-hidden.
const TempPasswordInfo = ({ member }) => {
  const [pos, setPos] = useState(null); // {top, left} when open, null when closed
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  const toggle = () => {
    if (pos) {
      setPos(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX });
  };

  useEffect(() => {
    if (!pos) return undefined;
    const onMouseDown = (e) => {
      if (buttonRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setPos(null);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPos(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pos]);

  if (!member.temp_password) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={`Show one-time password for ${member.email}`}
        aria-expanded={!!pos}
        title="One-time password"
        className="flex items-center justify-center w-5 h-5 p-0 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/40 hover:bg-amber-500/30 hover:text-amber-200 transition-colors duration-150"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {pos &&
        createPortal(
          <div
            ref={popoverRef}
            style={{ top: pos.top, left: pos.left }}
            className="absolute z-50 bg-gray-900 border border-purple-500/30 rounded-lg px-4 py-3 shadow-2xl whitespace-nowrap"
          >
            <p className="text-xs font-medium text-gray-400 mb-1">One-time password</p>
            <PasswordChip password={member.temp_password} />
            <p className="text-[11px] text-amber-300/80 mt-2">
              {isOneTime(member.role)
                ? 'Valid until the survey is submitted'
                : 'Must be changed at first login'}
            </p>
          </div>,
          document.body,
        )}
    </>
  );
};

export default TempPasswordInfo;
